/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

const HINT_CHIP_MS = 20000;

const PORTAL_BUILD_RADIUS = 2.75;
const ART_DOORWAY_M = 10;
const HUD_TICK_SECONDS = 0.4;
const FOCUS_GLIDE_MS = 900;
const RETURN_GLIDE_MS = 700;

const POS_KEY = 'airx:museum:pos';
const POS_SAVE_SECONDS = 2;
const GATE_AFTER_SECONDS = 120;
const GATE_TIME_KEY = 'airx:museum:walkTime';
const POS_MAX_AGE_MS = 30 * 60 * 1000;
const POS_MAX_RADIUS_M = 75;
const CONTEXT_LOSS_LITE_AFTER = 2;
const STEP_FAILURE_LIMIT = 120;

const COLLECTIONS_ENDPOINT = '/collections.json?limit=250';

const WINGS_FETCH_TIMEOUT_MS = 5000;
const MAX_WINGS = 8;
const MIN_WING_PRODUCTS = 6;
const EXCLUDED_WINGS = { 'new-releases-archive': 1, frontpage: 1 };

const SIZE_RE = /(\d+(?:\.\d+)?)L"x(\d+(?:\.\d+)?)H"/;

const prettySize = (value) => {
  const m = String(value).match(SIZE_RE);
  return m ? `${m[1]}" × ${m[2]}"` : String(value);
};

const stripHtml = (html) => {

  try {
    return new DOMParser().parseFromString(String(html || ''), 'text/html').body.textContent.trim();
  } catch (err) {
    return '';
  }
};

const shuffleWings = (list) => {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = list[i];
    list[i] = list[j];
    list[j] = tmp;
  }
  return list;
};

export const composeWingLineup = (curated, fetched, fallback) => {
  if (!Array.isArray(fetched)) return fallback;
  const lineup = (curated || []).slice(0, MAX_WINGS);
  const seen = {};
  lineup.forEach((w) => { seen[w.key] = 1; });
  const pool = shuffleWings(fetched.slice());
  for (let i = 0; i < pool.length && lineup.length < MAX_WINGS; i++) {
    const w = pool[i];
    if (!w || !w.key || seen[w.key]) continue;
    seen[w.key] = 1;
    lineup.push({ key: w.key, title: w.title });
  }
  return lineup.length >= 2 ? lineup : fallback;
};

export const validateSavedWings = (wings) => {
  if (!Array.isArray(wings) || !wings.length || wings.length > MAX_WINGS) return null;
  const out = [];
  for (let i = 0; i < wings.length; i++) {
    const w = wings[i];
    if (!w || typeof w.key !== 'string' || !w.key) return null;
    if (typeof w.title !== 'string' || !w.title) return null;
    out.push({ key: w.key, title: w.title });
  }
  return out;
};

const supportsWebGL = () => {
  try {
    const probe = document.createElement('canvas');
    return !!(probe.getContext('webgl2') || probe.getContext('webgl'));
  } catch (err) {
    return false;
  }
};

export class Gallery3D extends HTMLElement {
  connectedCallback() {
    if (this.booted) return;
    this.booted = true;
    this.disposed = false;

    const $ = (sel) => this.querySelector(sel);
    this.refs = {
      fallback: $('[data-gallery-fallback]'),
      stage: $('[data-gallery-stage]'),
      veil: $('[data-gallery-veil]'),
      loading: $('[data-gallery-loading]'),
      progress: $('[data-gallery-progress]'),
      progressBar: $('[data-gallery-progress-bar]'),
      progressLabel: $('[data-gallery-progress-label]'),
      enter: $('[data-gallery-enter]'),
      enterBtn: $('[data-gallery-enter-btn]'),
      hud: $('[data-gallery-hud]'),
      hudWing: $('[data-hud-wing]'),
      hudMap: $('[data-hud-map]'),
      hudHelp: $('[data-hud-help]'),
      hudLeave: $('[data-hud-leave]'),
      hudEscTip: $('[data-hud-esc-tip]'),
      shimmer: $('[data-gallery-shimmer]'),
      crosshair: $('[data-hud-crosshair]'),
      joystick: $('[data-hud-joystick]'),
      move: $('[data-hud-move]'),
      hint: $('[data-hud-hint]'),
      gate: $('[data-gallery-gate]'),
      restore: $('[data-hud-restore]'),
      toast: $('[data-hud-toast]'),
      map: $('[data-gallery-map]'),
      mapSvg: $('[data-map-svg]'),
      mapClose: $('[data-map-close]'),
      panel: $('[data-gallery-panel]'),
      panelClose: $('[data-panel-close]'),
      panelEsc: $('[data-panel-esc]'),
      panelChip: $('[data-panel-chip]'),
      panelRating: $('[data-panel-rating]'),
      panelRatingText: $('[data-panel-rating-text]'),
      panelTitle: $('[data-panel-title]'),
      panelSkeleton: $('[data-panel-skeleton]'),
      panelContent: $('[data-panel-content]'),
      panelExcerpt: $('[data-panel-excerpt]'),
      panelExcerptText: $('[data-panel-excerpt-text]'),
      panelMore: $('[data-panel-more]'),
      panelPrice: $('[data-panel-price]'),
      panelSizesWrap: $('[data-panel-sizes-wrap]'),
      panelSizes: $('[data-panel-sizes]'),
      panelDepthWrap: $('[data-panel-depth-wrap]'),
      panelDepth: $('[data-panel-depth]'),
      panelQty: $('[data-panel-qty]'),
      panelAtc: $('[data-panel-atc]'),
      panelAtcLabel: $('[data-panel-atc-label]'),
      panelError: $('[data-form-error]'),
      panelDetails: $('[data-panel-details]'),
    };

    try {
      this.wings = JSON.parse(this.dataset.wings || '[]');
    } catch (err) {
      this.wings = [];
    }

    this.wingsCurated = this.dataset.wingsCurated === 'true';
    const stringsEl = $('[data-gallery-strings]');
    try {
      this.strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};
    } catch (err) {
      this.strings = {};
    }

    this.isTouch = !!(
      window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches
    );

    this.onContextMenu = (e) => {

      if (e.target.closest('input, textarea, select, [data-gallery-panel]')) return;
      e.preventDefault();
    };
    this.addEventListener('contextmenu', this.onContextMenu);
    this.querySelectorAll('img').forEach((img) => {
      img.draggable = false;
      img.setAttribute('draggable', 'false');
    });

    if (this.dataset.fallbackUrl && this.refs.hudLeave) {
      this.refs.hudLeave.href = this.dataset.fallbackUrl;
    }

    this.applyLiveRating();

    this.entered = false;
    this.focusedMesh = null;
    this.focusing = false;
    this.panelOpen = false;
    this.mapOpen = false;
    this.helpOpen = false;
    this.hintShown = false;

    this.gateOpen = false;
    this.loggedIn = this.dataset.loggedIn === 'true';
    this.walkSeconds = 0;
    try {
      this.walkSeconds = parseFloat(sessionStorage.getItem(GATE_TIME_KEY)) || 0;
    } catch (err) {  }
    this.openDrawers = new Set();
    this.wingState = new Map();
    this.wingBuilt = new Map();
    this.currentZone = null;
    this.activeZones = null;
    this.wingLoadingCount = 0;
    this.timers = new Set();
    this.raf = null;
    this.hudClock = 0;
    this.lastFocusAt = 0;
    this.posClock = 0;
    this.resumePos = null;
    this.resumePending = false;
    this.contextLosses = 0;
    this.contextLost = false;
    this.contextLossDegraded = false;
    this.stepFailures = 0;
    this.hintRaf = null;
    this.restoreRaf = null;
    this.travelSeq = 0;

    if (!this.canRun()) return;

    this.classList.add('is-immersive');
    if (this.refs.fallback) {
      this.refs.fallback.classList.add('visually-hidden-focusable');

      this.refs.fallback.querySelectorAll('a, button, [tabindex]').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
    }

    this.refs.loading.classList.remove('is-done');
    this.setProgress(0, '');
    this.refs.enter.hidden = true;
    this.refs.hud.hidden = true;
    if (this.refs.hint) {
      this.refs.hint.hidden = true;
      this.refs.hint.classList.remove('is-visible');
    }
    if (this.refs.restore) {
      this.refs.restore.hidden = true;
      this.refs.restore.classList.remove('is-visible');
    }
    if (this.refs.move) this.refs.move.hidden = true;
    this.refs.map.hidden = true;
    this.refs.map.classList.remove('is-open');
    this.refs.panel.classList.remove('is-open');
    this.refs.panel.setAttribute('aria-hidden', 'true');
    this.refs.veil.hidden = true;
    this.refs.veil.classList.remove('is-active');
    this.refs.loading.hidden = false;

    this.bindDocumentEvents();
    this.boot().catch(() => this.showFallback());
  }

  disconnectedCallback() {
    this.teardown();
    this.booted = false;
  }

  canRun() {
    if (!this.refs.stage || !this.wings.length) return false;
    if (!document.documentElement.classList.contains('motion')) return false;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return supportsWebGL();
  }

  pickQuality() {
    const memory = navigator.deviceMemory || 8;
    const lowEndUA = /\b(android [4-8]\.|iphone os (?:9|1[0-2])_)/i.test(navigator.userAgent);
    return this.isTouch || memory <= 4 || lowEndUA ? 'lite' : 'high';
  }

  dprCap() {
    return this.isTouch || this.quality === 'lite' || this.contextLossDegraded ? 1.75 : 2;
  }

  setProgress(pct, label) {
    if (this.refs.progressBar) this.refs.progressBar.style.width = `${pct}%`;
    if (this.refs.progress) this.refs.progress.setAttribute('aria-valuenow', String(Math.round(pct)));
    if (label && this.refs.progressLabel) this.refs.progressLabel.textContent = label;
  }

  async boot() {

    const gen = (this.bootGen = (this.bootGen || 0) + 1);
    const stale = () => this.disposed || gen !== this.bootGen;

    this.setProgress(3, this.strings.loadingEngine);

    this.resumePos = this.readSavedPos();
    const wingsReady = this.resolveWings(gen);

    const threeMod = await import(this.dataset.threeSrc);
    const THREE = threeMod.default || threeMod;
    if (stale()) return;
    if (!THREE || !THREE.WebGLRenderer) throw new Error('three unavailable');
    this.THREE = THREE;
    this.setProgress(30, this.strings.loadingWorld);

    const [worldMod, controlsMod, artMod] = await Promise.all([
      import('./gallery-3d-world.js'),
      import('./gallery-3d-controls.js'),
      import('./gallery-3d-art.js'),
    ]);
    if (stale()) return;

    await wingsReady;
    if (stale()) return;

    this.quality = this.pickQuality();

    this.renderer = new THREE.WebGLRenderer({
      antialias: this.quality === 'high',
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.dprCap()));
    if ('outputColorSpace' in this.renderer && THREE.SRGBColorSpace) {
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    }
    this.refs.stage.hidden = false;
    this.refs.stage.appendChild(this.renderer.domElement);
    this.renderer.domElement.setAttribute('aria-hidden', 'true');

    const w = this.refs.stage.clientWidth || 1;
    const h = this.refs.stage.clientHeight || 1;
    this.camera = new THREE.PerspectiveCamera(72, w / h, 0.08, 150);

    this.world = worldMod.createWorld(THREE, {
      renderer: this.renderer,
      quality: this.quality,
      wingCount: this.wings.length,
    });
    this.controls = controlsMod.createControls(THREE, {
      camera: this.camera,
      dom: this.refs.stage,
      getColliders: () => this.world.colliders,
      isTouch: this.isTouch,
      quality: this.quality,
    });
    this.art = artMod.createArtManager(THREE, {
      scene: this.world.scene,
      quality: this.quality,
      moneyFormat: window.AirX && window.AirX.formatMoney,
    });

    this.wings.forEach((wing, i) => this.world.setWingLabel(i, wing.title));
    this.controls.teleport(this.world.lobbySpawn.position, this.world.lobbySpawn.yaw);
    this.lobbyPosition = this.world.lobbySpawn.position.clone();

    this.controls.onFloorClick((point) => this.onFloorClick(point));
    if (this.isTouch && this.controls.joystick) {
      this.refs.joystick.hidden = false;
      this.controls.joystick.attach(this.refs.joystick);
    }

    this.raycaster = new THREE.Raycaster();
    this.ndc = new THREE.Vector2();
    this.tmpVec = new THREE.Vector3();

    this.setProgress(50, this.strings.loadingArt);
    await this.buildWing(0, true);
    if (stale()) return;
    this.syncZones(true);
    this.setProgress(100, '');

    this.bindStageEvents();
    this.observeSize();
    this.refs.panel.hidden = false;

    this.resize();
    this.startLoop();

    if (this.resumePos && this.refs.enterBtn && this.strings.resume) {
      this.refs.enterBtn.textContent = this.strings.resume;
    }
    this.refs.loading.classList.add('is-done');
    this.setTimer(() => {
      this.refs.loading.hidden = true;
      this.refs.enter.hidden = false;
    }, 450);
  }

  async resolveWings(gen) {

    if (this.resumePos && this.resumePos.wings) {
      this.wings = this.resumePos.wings;
      return;
    }
    const fallback = this.wings.slice(0, MAX_WINGS);
    const curated = this.wingsCurated ? fallback : [];
    const fetched = await this.fetchPublishedWings();

    if (this.disposed || gen !== this.bootGen) return;
    this.wings = composeWingLineup(curated, fetched, fallback);
  }

  async fetchPublishedWings() {
    if (typeof fetch !== 'function') return null;

    const ctrl = 'AbortController' in window ? new AbortController() : null;
    this.wingsAbort = ctrl;
    if (ctrl) this.setTimer(() => ctrl.abort(), WINGS_FETCH_TIMEOUT_MS);
    const init = { headers: { Accept: 'application/json' } };
    if (ctrl) init.signal = ctrl.signal;
    try {
      const res = await fetch(COLLECTIONS_ENDPOINT, init);
      if (!res.ok) return null;
      const data = await res.json();
      const out = [];
      ((data && data.collections) || []).forEach((c) => {
        if (!c || !c.handle || EXCLUDED_WINGS[c.handle]) return;

        if (typeof c.products_count === 'number' && c.products_count < MIN_WING_PRODUCTS) return;
        out.push({ key: c.handle, title: c.title || c.handle });
      });
      return out;
    } catch (err) {
      return null;
    }
  }

  showFallback() {
    if (this.disposed) return;
    this.teardown3D();
    this.classList.remove('is-immersive');
    if (this.refs.fallback) {
      this.refs.fallback.classList.remove('visually-hidden-focusable');
      this.refs.fallback.querySelectorAll('[tabindex="-1"]').forEach((el) => {
        el.removeAttribute('tabindex');
      });
    }
    ['loading', 'enter', 'hud', 'map', 'stage', 'veil'].forEach((key) => {
      if (this.refs[key]) this.refs[key].hidden = true;
    });
    if (this.refs.panel) {
      this.refs.panel.hidden = true;
      this.refs.panel.classList.remove('is-open');
    }
  }

  bindDocumentEvents() {
    this.onKeyDown = (e) => this.handleKey(e);
    this.onVisibility = () => {
      if (document.hidden) {
        this.savePos();
        this.stopLoop();
      } else this.startLoop();
    };
    this.onSectionUnload = (e) => {
      if (e.target && e.target.contains && e.target.contains(this)) this.teardown();
    };
    this.onLockChange = () => {
      this.updateCrosshair();

      if (this.refs.hudEscTip) {
        this.refs.hudEscTip.hidden = !(this.controls && this.controls.isLocked());
      }
    };
    this.onPageHide = () => this.savePos();
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('shopify:section:unload', this.onSectionUnload);
    document.addEventListener('pointerlockchange', this.onLockChange);
    window.addEventListener('pagehide', this.onPageHide);

    if (window.AirX) {
      this.onDrawerOpen = (e) => {
        this.openDrawers.add((e.detail && e.detail.id) || 'drawer');

        if (!this.isTouch && this.controls && this.controls.isLocked()) {
          this.relockOnResume = true;
        }
        if (this.entered && this.controls) this.controls.disable();
        this.updateCrosshair();
      };
      this.onDrawerClose = (e) => {
        this.openDrawers.delete((e.detail && e.detail.id) || 'drawer');
        if (this.openDrawers.size) return;
        if (this.panelOpen) {

          window.AirX.trapFocus(this.refs.panel, this.refs.panelClose);
        } else if (this.entered && !this.focusedMesh && !this.mapOpen && !this.helpOpen) {
          this.controls.enable();
          if (this.relockOnResume) {
            this.relockOnResume = false;
            this.controls.lock();
          }
        }
        this.updateCrosshair();
      };
      window.AirX.on('drawer:open', this.onDrawerOpen);
      window.AirX.on('drawer:close', this.onDrawerClose);
    }
  }

  bindStageEvents() {
    this.onStageClick = (e) => this.handleStageClick(e);
    this.refs.stage.addEventListener('click', this.onStageClick);

    this.onStageRelock = () => {
      if (
        this.entered && !this.isTouch && !this.gateOpen && !this.panelOpen && !this.focusedMesh &&
        !this.focusing && !this.mapOpen && !this.helpOpen && !this.openDrawers.size &&
        this.controls && !this.controls.isLocked()
      ) {
        this.controls.lock();
      }
    };
    this.refs.stage.addEventListener('click', this.onStageRelock);

    this.onEnterClick = () => this.enter();
    this.onHudMapClick = () => this.toggleMap();
    this.onHudHelpClick = () => this.openHelp();
    this.onMapCloseClick = () => this.closeMap();
    this.onPanelCloseClick = () => this.exitFocus();
    this.onPanelMoreClick = () => this.toggleExcerpt();
    this.onPanelAtcClick = () => this.addToCart();

    this.refs.enterBtn.addEventListener('click', this.onEnterClick);
    this.refs.hudMap.addEventListener('click', this.onHudMapClick);
    if (this.refs.hudHelp) this.refs.hudHelp.addEventListener('click', this.onHudHelpClick);
    this.refs.mapClose.addEventListener('click', this.onMapCloseClick);
    this.refs.panelClose.addEventListener('click', this.onPanelCloseClick);
    this.refs.panelMore.addEventListener('click', this.onPanelMoreClick);
    this.refs.panelAtc.addEventListener('click', this.onPanelAtcClick);

    this.onStageFirstMove = () => this.hideHint();
    this.refs.stage.addEventListener('pointerdown', this.onStageFirstMove);
    if (this.refs.joystick) this.refs.joystick.addEventListener('pointerdown', this.onStageFirstMove);

    this.bindMoveButtons();

    this.onContextLost = (e) => {
      e.preventDefault();
      this.contextLosses += 1;

      this.contextLost = true;
      this.stopLoop();
      this.showRestoring();
    };
    this.onContextRestored = () => {
      this.contextLost = false;
      if (this.contextLosses > CONTEXT_LOSS_LITE_AFTER && !this.contextLossDegraded) {
        this.contextLossDegraded = true;
        if (this.art && this.art.setTextureQuality) this.art.setTextureQuality('lite');
        if (this.renderer) {
          this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.dprCap()));
        }
      }
      this.resize();
      this.hideRestoring();
      this.startLoop();
    };
    this.renderer.domElement.addEventListener('webglcontextlost', this.onContextLost);
    this.renderer.domElement.addEventListener('webglcontextrestored', this.onContextRestored);
  }

  bindMoveButtons() {
    this.moveCleanups = [];
    if (!this.refs.move) return;

    const held = { forward: false, back: false, left: false, right: false };
    const applyHeld = () => {
      if (!this.controls) return;
      this.controls.setExternalMove(0, (held.forward ? 1 : 0) - (held.back ? 1 : 0));
      this.controls.setExternalTurn((held.right ? 1 : 0) - (held.left ? 1 : 0));
    };
    this.refs.move.querySelectorAll('[data-move]').forEach((btn) => {
      const dir = btn.dataset.move;
      if (!(dir in held)) return;
      const down = (e) => {
        e.preventDefault();
        if (!this.entered || !this.controls) return;
        try { btn.setPointerCapture(e.pointerId); } catch (err) {  }
        btn.classList.add('is-held');
        this.hideHint();
        held[dir] = true;
        applyHeld();
      };
      const up = () => {
        btn.classList.remove('is-held');
        held[dir] = false;
        applyHeld();
      };
      const menu = (e) => e.preventDefault();
      btn.addEventListener('pointerdown', down);
      btn.addEventListener('pointerup', up);
      btn.addEventListener('pointercancel', up);
      btn.addEventListener('contextmenu', menu);
      this.moveCleanups.push(() => {
        btn.removeEventListener('pointerdown', down);
        btn.removeEventListener('pointerup', up);
        btn.removeEventListener('pointercancel', up);
        btn.removeEventListener('contextmenu', menu);
      });
    });
  }

  observeSize() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.refs.stage);
    } else {
      this.onWindowResize = () => this.resize();
      window.addEventListener('resize', this.onWindowResize);
    }
    if ('IntersectionObserver' in window) {
      this.viewObserver = new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) this.startLoop();
        else this.stopLoop();
      });
      this.inView = true;
      this.viewObserver.observe(this);
    } else {
      this.inView = true;
    }
  }

  resize() {
    if (!this.renderer) return;
    const w = this.refs.stage.clientWidth || 1;
    const h = this.refs.stage.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  setTimer(fn, ms) {
    const id = setTimeout(() => {
      this.timers.delete(id);
      fn();
    }, ms);
    this.timers.add(id);
    return id;
  }

  enter() {
    const resuming = this.entered;
    if (this.helpOpen && window.AirX) window.AirX.releaseFocus(this.refs.enter);
    this.helpOpen = false;
    this.entered = true;
    this.refs.enter.hidden = true;
    this.refs.hud.hidden = false;
    if (this.refs.move) this.refs.move.hidden = !this.isTouch;
    if (!resuming && this.resumePos) {
      const saved = this.resumePos;
      this.resumePos = null;
      this.resumeAt(saved);
    }
    if (!this.panelOpen && !this.focusedMesh && !this.mapOpen && !this.openDrawers.size) {
      this.controls.enable();
      if (!this.isTouch) this.controls.lock();
    }
    this.updateCrosshair();
    if (!resuming) this.showHint();
  }

  tickGate(seconds) {
    if (this.loggedIn || this.gateOpen || !this.refs.gate) return;
    this.walkSeconds += seconds;
    try { sessionStorage.setItem(GATE_TIME_KEY, String(this.walkSeconds)); } catch (err) {  }
    if (this.walkSeconds < GATE_AFTER_SECONDS) return;
    if (this.panelOpen || this.focusedMesh || this.focusing || this.openDrawers.size || this.mapOpen || this.helpOpen) return;
    this.openGate();
  }

  openGate() {
    this.gateOpen = true;
    this.hideHint();
    this.controls.disable();
    this.savePos();
    this.refs.gate.hidden = false;
    if (window.AirX) window.AirX.trapFocus(this.refs.gate, this.refs.gate.querySelector('a'));
    this.updateCrosshair();
  }

  openHelp() {
    if (!this.entered || this.gateOpen || this.helpOpen || this.panelOpen || this.focusing || this.focusedMesh) return;
    if (this.mapOpen) this.closeMap();
    this.helpOpen = true;
    this.hideHint();
    this.controls.disable();
    if (this.refs.enterBtn && this.strings.resume) this.refs.enterBtn.textContent = this.strings.resume;
    this.refs.enter.hidden = false;
    if (window.AirX) window.AirX.trapFocus(this.refs.enter, this.refs.enterBtn);
    this.updateCrosshair();
  }

  showHint() {
    const el = this.refs.hint;
    if (!el || this.hintShown) return;

    if (this.refs.restore && !this.refs.restore.hidden) return;
    this.hintShown = true;
    el.textContent = (this.isTouch ? this.strings.hintChipTouch : this.strings.hintChip) || '';
    if (!el.textContent) return;
    el.hidden = false;
    this.hintRaf = requestAnimationFrame(() => {
      this.hintRaf = null;
      el.classList.add('is-visible');
    });
    this.hintTimer = this.setTimer(() => this.hideHint(), HINT_CHIP_MS);
  }

  hideHint() {
    const el = this.refs.hint;
    if (!el || el.hidden) return;

    if (this.hintRaf) {
      cancelAnimationFrame(this.hintRaf);
      this.hintRaf = null;
    }
    if (this.hintTimer) {
      clearTimeout(this.hintTimer);
      this.timers.delete(this.hintTimer);
      this.hintTimer = null;
    }
    el.classList.remove('is-visible');
    this.setTimer(() => {
      if (!el.classList.contains('is-visible')) el.hidden = true;
    }, 320);
  }

  showRestoring() {
    this.hideHint();
    const el = this.refs.restore;
    if (!el) return;
    el.hidden = false;
    this.restoreRaf = requestAnimationFrame(() => {
      this.restoreRaf = null;
      el.classList.add('is-visible');
    });
  }

  hideRestoring() {
    const el = this.refs.restore;
    if (!el || el.hidden) return;

    if (this.restoreRaf) {
      cancelAnimationFrame(this.restoreRaf);
      this.restoreRaf = null;
    }
    el.classList.remove('is-visible');
    this.setTimer(() => {
      if (!el.classList.contains('is-visible')) el.hidden = true;
    }, 320);
  }

  readSavedPos() {
    try {
      const raw = window.sessionStorage && sessionStorage.getItem(POS_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || data.entered !== true) return null;
      if (typeof data.t !== 'number' || Date.now() - data.t > POS_MAX_AGE_MS) return null;

      if (!Number.isFinite(data.x) || !Number.isFinite(data.z) || !Number.isFinite(data.yaw)) {
        return null;
      }
      if (Math.hypot(data.x, data.z) > POS_MAX_RADIUS_M) return null;

      const wings = validateSavedWings(data.wings);
      if (!wings) return null;
      data.wings = wings;
      return data;
    } catch (err) {
      return null;
    }
  }

  savePos() {
    if (!this.entered || this.disposed || this.resumePending || !this.camera) return;
    try {
      sessionStorage.setItem(
        POS_KEY,
        JSON.stringify({
          x: this.camera.position.x,
          z: this.camera.position.z,

          yaw: this.camera.rotation.y,
          entered: true,
          t: Date.now(),

          wings: this.wings.map((w) => ({ key: w.key, title: w.title })),
        })
      );
    } catch (err) {

    }
  }

  async resumeAt(saved) {
    this.resumePending = true;

    const seq = this.travelSeq;
    const veil = this.refs.veil;
    veil.hidden = false;
    void veil.offsetWidth;
    veil.classList.add('is-active');
    try {
      const pos = new this.THREE.Vector3(saved.x, 0, saved.z);
      const zone = this.world.zoneAt(pos);
      const W = this.wings.length;
      const needed = [];
      if (zone.indexOf('spoke-') === 0) {
        needed.push(parseInt(zone.slice(6), 10));
      } else if (zone.indexOf('arc-') === 0) {
        const j = parseInt(zone.slice(4), 10);
        needed.push(j, (j + 1) % W);
      }
      await Promise.all(needed.map((i) => this.buildWing(i)));
      if (this.disposed || seq !== this.travelSeq) return;
      if (needed.every((i) => this.wingBuilt.has(i))) {
        this.controls.teleport(pos, saved.yaw);
        this.syncZones(true);
      }
    } catch (err) {

    } finally {
      this.resumePending = false;
      if (!this.disposed && seq === this.travelSeq) {
        veil.classList.remove('is-active');
        this.setTimer(() => {
          if (!veil.classList.contains('is-active')) veil.hidden = true;
        }, 340);
      }
    }
  }

  startLoop() {
    if (this.raf || this.disposed || !this.renderer || this.contextLost) return;
    if (document.hidden || this.inView === false) return;
    this.lastT = performance.now();
    const tick = (t) => {
      this.raf = requestAnimationFrame(tick);

      try {
        this.step(t);
        this.stepFailures = 0;
      } catch (err) {

        if (this.stepFailures === 0) console.warn('gallery-3d: frame step failed', err);
        this.stepFailures += 1;
        if (this.stepFailures >= STEP_FAILURE_LIMIT) {
          console.warn(`gallery-3d: render loop stopped after ${STEP_FAILURE_LIMIT} consecutive frame failures`);
          this.stopLoop();
        }
      }
    };
    this.raf = requestAnimationFrame(tick);
  }

  stopLoop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  }

  step(t) {
    const dt = Math.min((t - this.lastT) / 1000 || 0, 0.1);
    this.lastT = t;

    this.controls.update(dt);
    this.syncZones();
    this.world.update(dt, this.camera.position);
    this.art.updateLOD(this.camera.position, this.currentZone);

    this.hudClock += dt;
    if (this.hudClock >= HUD_TICK_SECONDS) {
      this.hudClock = 0;
      this.checkWingProximity();
      this.ensureZoneArt();
      this.updateAim();
      if (this.mapOpen) this.updateMapPlayer();
    }

    this.posClock += dt;
    if (this.posClock >= POS_SAVE_SECONDS) {
      this.posClock = 0;
      this.savePos();
      this.tickGate(POS_SAVE_SECONDS);
    }

    this.renderer.render(this.world.scene, this.camera);
  }

  zoneNeighbors(zone) {
    const W = this.wings.length;
    if (zone === 'hub') return this.wings.map((w, i) => 'spoke-' + i);
    if (zone.indexOf('spoke-') === 0) {
      const i = parseInt(zone.slice(6), 10);
      const out = ['hub'];
      if (W >= 2) {
        const prev = 'arc-' + ((i - 1 + W) % W);
        const next = 'arc-' + i;
        out.push(prev);
        if (next !== prev) out.push(next);
      }
      return out;
    }
    if (zone.indexOf('arc-') === 0) {
      const j = parseInt(zone.slice(4), 10);
      const out = [];
      const push = (id) => { if (out.indexOf(id) === -1) out.push(id); };
      push('spoke-' + j);
      push('spoke-' + ((j + 1) % W));

      push('arc-' + ((j - 1 + W) % W));
      push('arc-' + ((j + 1) % W));
      return out;
    }
    return [];
  }

  syncZones(force) {
    if (!this.world || !this.world.zoneAt) return;
    const zone = this.world.zoneAt(this.camera.position);
    if (!force && zone === this.currentZone) return;
    this.currentZone = zone;
    const active = [zone].concat(this.zoneNeighbors(zone).filter((id) => id !== zone));
    this.activeZones = active;
    this.world.setActiveZones(active);
    this.art.setActiveZones(active);

    const W = this.wings.length;
    if (zone.indexOf('spoke-') === 0) {
      const i = parseInt(zone.slice(6), 10);
      this.buildWing(i);
      if (W >= 2) {
        this.buildWing((i - 1 + W) % W);
        this.buildWing((i + 1) % W);
      }
    } else if (zone.indexOf('arc-') === 0) {
      const j = parseInt(zone.slice(4), 10);
      this.buildWing(j);
      this.buildWing((j + 1) % W);
    }
    this.ensureZoneArt();
    this.updateWingTitle();
  }

  ensureZoneArt() {
    if (!this.art || !this.activeZones) return;
    const pos = this.camera.position;
    const W = this.wings.length;
    this.activeZones.forEach((id) => {
      if (id === 'hub' || this.art.zonePlaced(id)) return;
      let near = id === this.currentZone;
      if (!near) {
        const doors = this.world.zoneDoorways(id) || [];
        for (let k = 0; k < doors.length && !near; k++) {
          if (doors[k].distanceTo(pos) < ART_DOORWAY_M) near = true;
        }
      }
      if (!near) return;
      if (id.indexOf('spoke-') === 0) {
        const i = parseInt(id.slice(6), 10);
        const built = this.wingBuilt.get(i);
        if (built && this.wings[i]) {
          this.art.placeWing(this.wings[i], built.slots, built.heroSlot, id);
        }
      } else if (id.indexOf('arc-') === 0) {
        const j = parseInt(id.slice(4), 10);
        const arc = this.world.buildArc ? this.world.buildArc(j) : null;
        if (!arc) return;
        const feeds = [j, (j + 1) % W].map((w) => {
          const built = this.wingBuilt.get(w);
          return {
            key: this.wings[w] ? this.wings[w].key : '',
            start: built ? built.slots.length + 1 : undefined,
          };
        });
        this.art.placeRing(id, arc.slots, feeds);
      }
    });
  }

  buildWing(index, placeNow) {
    const wing = this.wings[index];
    if (!wing) return Promise.resolve();
    if (this.wingState.has(wing.key)) return this.wingState.get(wing.key);

    const promise = (async () => {
      this.setWingLoading(1);
      try {
        const { count } = await this.art.loadWing(wing);
        if (this.disposed) return;
        if (!count) {

          this.wingState.delete(wing.key);
          return;
        }
        const built = this.world.buildWing(index, { key: wing.key, title: wing.title, count });
        if (!built) {
          this.wingState.delete(wing.key);
          return;
        }
        this.wingBuilt.set(index, built);
        if (placeNow) {

          this.art.placeWing(wing, built.slots, built.heroSlot, 'spoke-' + index);
        }

        this.syncZones(true);
      } catch (err) {
        this.wingState.delete(wing.key);
      } finally {
        this.setWingLoading(-1);
      }
    })();
    this.wingState.set(wing.key, promise);
    return promise;
  }

  setWingLoading(delta) {
    this.wingLoadingCount = Math.max(0, this.wingLoadingCount + delta);
    if (this.refs.shimmer) this.refs.shimmer.hidden = this.wingLoadingCount === 0;
  }

  checkWingProximity() {
    if (!this.entered) return;

    const entrances = this.world.wingEntrances || [];
    for (let i = 0; i < entrances.length; i++) {
      const wing = this.wings[i];
      if (!wing || this.wingState.has(wing.key)) continue;
      if (entrances[i].position.distanceTo(this.camera.position) < PORTAL_BUILD_RADIUS) {
        this.buildWing(i);
      }
    }
  }

  updateWingTitle() {
    if (!this.refs.hudWing) return;
    const zone = this.currentZone || 'hub';
    let title = this.strings.rotunda || '';
    if (zone.indexOf('spoke-') === 0) {
      const wing = this.wings[parseInt(zone.slice(6), 10)];
      if (wing && wing.title) title = wing.title;
    } else if (zone.indexOf('arc-') === 0) {
      const j = parseInt(zone.slice(4), 10);
      const a = this.wings[j];
      const b = this.wings[(j + 1) % this.wings.length];
      if (a && b) {
        title = (this.strings.ringTitle || '[a] ↔ [b]')
          .replace('[a]', a.title)
          .replace('[b]', b.title);
      }
    }
    if (title !== this.refs.hudWing.textContent) this.refs.hudWing.textContent = title;
  }

  updateCrosshair() {
    if (!this.refs.crosshair) return;
    const visible =
      this.entered &&
      !this.isTouch &&
      !this.panelOpen &&
      !this.focusedMesh &&
      !this.mapOpen &&
      !this.helpOpen &&
      !this.openDrawers.size &&
      this.controls &&
      this.controls.isLocked();
    this.refs.crosshair.hidden = !visible;
  }

  updateAim() {
    if (this.refs.crosshair.hidden) return;
    const targets = this.art.raycastTargets();
    if (!targets || !targets.length) return;
    this.ndc.set(0, 0);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const hits = this.raycaster.intersectObjects(targets, false);
    const aimed = hits.length > 0 && hits[0].distance < 9;
    this.refs.crosshair.classList.toggle('is-active', aimed);
  }

  onFloorClick(point) {
    if (!this.entered || this.focusing || this.focusedMesh || this.panelOpen || this.mapOpen || this.helpOpen) return;
    if (this.openDrawers.size) return;
    if (performance.now() - this.lastFocusAt < 400) return;

    const THREE = this.THREE;
    const destination = new THREE.Vector3(point.x, this.camera.position.y, point.z);
    const direction = this.camera.getWorldDirection(this.tmpVec);
    const lookAt = destination.clone().add(direction);
    const ms = Math.min(1600, Math.max(500, destination.distanceTo(this.camera.position) * 240));
    this.controls.glideTo(destination, lookAt, ms).catch(() => {});
  }

  handleStageClick(e) {
    if (!this.entered || this.gateOpen || this.focusing || this.mapOpen || this.helpOpen || this.openDrawers.size) return;
    if (this.panelOpen) {
      this.exitFocus();
      return;
    }
    const targets = this.art.raycastTargets();
    if (!targets || !targets.length) return;

    if (this.controls.isLocked()) {
      this.ndc.set(0, 0);
    } else {
      const rect = this.refs.stage.getBoundingClientRect();
      this.ndc.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -(((e.clientY - rect.top) / rect.height) * 2 - 1)
      );
    }
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const hits = this.raycaster.intersectObjects(targets, false);
    if (hits.length && hits[0].distance < 14) {
      this.lastFocusAt = performance.now();
      this.focusOn(hits[0].object);
    }
  }

  async focusOn(mesh) {
    const info = this.art.artFor(mesh);
    if (!info) return;
    this.focusing = true;
    this.focusedMesh = mesh;

    const direction = this.camera.getWorldDirection(this.tmpVec);
    this.focusReturn = {
      position: this.camera.position.clone(),
      lookAt: this.camera.position.clone().add(direction),
    };

    this.relockOnResume = !this.isTouch && this.controls.isLocked();
    this.controls.disable();
    this.art.setFocused(mesh);
    this.updateCrosshair();

    try {
      await this.controls.glideTo(info.focusPoint, info.focusLookAt, FOCUS_GLIDE_MS);
    } catch (err) {

    }
    this.focusing = false;
    if (this.disposed || !this.focusedMesh) return;
    this.openPanel(info);
  }

  async exitFocus() {
    if (this.focusing || (!this.panelOpen && !this.focusedMesh)) return;
    this.focusing = true;
    this.closePanelDom();
    this.art.setFocused(null);

    if (this.focusReturn) {
      try {
        await this.controls.glideTo(this.focusReturn.position, this.focusReturn.lookAt, RETURN_GLIDE_MS);
      } catch (err) {

      }
    }
    this.focusedMesh = null;
    this.focusing = false;
    if (this.disposed) return;
    if (!this.openDrawers.size && !this.mapOpen && this.entered) {
      this.controls.enable();
      if (this.relockOnResume) {
        this.relockOnResume = false;
        this.controls.lock();
      }
    }
    this.updateCrosshair();
  }

  applyLiveRating() {
    const textEl = this.refs.panelRatingText;
    if (!textEl || !window.AirXStats || typeof window.AirXStats.get !== 'function') return;
    window.AirXStats.get().then((stats) => {
      if (!stats || this.disposed) return;
      const rating = stats.average.toFixed(1);
      const count = stats.count.toLocaleString();
      const stars = this.refs.panelRating && this.refs.panelRating.querySelector('.rating-stars');
      if (stars) {
        stars.style.setProperty('--rating-pct', `${stats.average * 20}%`);
        if (this.strings.starsLabel) {
          stars.setAttribute('aria-label', this.strings.starsLabel.replace('[rating]', rating));
        }
      }
      textEl.textContent = (this.strings.panelReviews || '[rating] · [count] reviews')
        .replace('[rating]', rating)
        .replace('[count]', count);
    });
  }

  openPanel(info) {
    const { panel } = this.refs;
    this.panelOpen = true;

    const wing = this.wings.find((w) => w.key === info.wingKey);
    this.refs.panelChip.textContent = wing ? wing.title : '';
    this.refs.panelTitle.textContent = info.product.title || '';
    this.refs.panelDetails.href = `/products/${info.product.handle}`;
    this.refs.panelError.hidden = true;
    this.refs.panelContent.hidden = true;
    this.refs.panelSkeleton.hidden = false;
    this.refs.panelQty.value = 1;

    this.product = null;
    this.variant = null;
    this.refs.panelAtc.disabled = true;
    this.refs.panelAtcLabel.textContent = this.strings.panelAdd;
    this.refs.panelPrice.textContent = '';
    this.refs.panelSizes.textContent = '';
    this.refs.panelDepth.textContent = '';
    this.refs.panelSizesWrap.hidden = true;
    this.refs.panelDepthWrap.hidden = true;
    this.refs.panelExcerpt.hidden = true;

    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');

    if (this.refs.panelEsc) this.refs.panelEsc.hidden = this.isTouch;
    if (window.AirX) window.AirX.trapFocus(panel, this.refs.panelClose);
    this.updateCrosshair();

    this.loadProduct(info.product.handle);
  }

  closePanelDom() {
    if (!this.panelOpen) return;
    this.panelOpen = false;
    if (this.fetchAbort) this.fetchAbort.abort();
    this.refs.panel.classList.remove('is-open');
    this.refs.panel.setAttribute('aria-hidden', 'true');
    if (window.AirX) window.AirX.releaseFocus(this.refs.panel);
  }

  async loadProduct(handle) {
    if (this.fetchAbort) this.fetchAbort.abort();
    this.fetchAbort = 'AbortController' in window ? new AbortController() : null;
    try {
      const res = await fetch(`/products/${handle}.js`, this.fetchAbort ? { signal: this.fetchAbort.signal } : undefined);
      if (!res.ok) throw new Error('product fetch failed');
      const product = await res.json();
      if (this.disposed || !this.panelOpen) return;
      this.buildCommerce(product);
      this.refs.panelSkeleton.hidden = true;
      this.refs.panelContent.hidden = false;
      this.retrapPanel();
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      if (this.disposed || !this.panelOpen) return;
      this.refs.panelSkeleton.hidden = true;

      this.showPanelError(this.strings.errorProduct);
      this.retrapPanel();
    }
  }

  retrapPanel() {
    if (window.AirX && !this.openDrawers.size) {
      window.AirX.trapFocus(this.refs.panel, this.refs.panelClose);
    }
  }

  showPanelError(message) {
    this.refs.panelError.textContent = message || '';
    this.refs.panelError.hidden = !message;
  }

  buildCommerce(product) {
    this.product = product;
    this.refs.panelTitle.textContent = product.title;
    this.refs.panelDetails.href = `/products/${product.handle}`;

    const text = stripHtml(product.description);
    if (text) {
      const words = text.split(/\s+/);
      this.excerptShort = words.slice(0, 40).join(' ') + (words.length > 40 ? '…' : '');
      this.excerptFull = text;
      this.excerptExpanded = false;
      this.refs.panelExcerptText.textContent = this.excerptShort;
      this.refs.panelMore.hidden = words.length <= 40;
      this.refs.panelMore.textContent = this.strings.more;
      this.refs.panelMore.setAttribute('aria-expanded', 'false');
      this.refs.panelExcerpt.hidden = false;
    } else {
      this.refs.panelExcerpt.hidden = true;
    }

    const rawOptions = product.options || [];
    const names = rawOptions.map((o) => (typeof o === 'string' ? o : o.name || ''));
    this.sizeIdx = names.findIndex((n) => /^sizes?$/i.test(n));
    this.depthIdx = names.findIndex((n) => /depth/i.test(n));

    const valuesAt = (idx) => {
      if (idx < 0) return [];
      const opt = rawOptions[idx];
      if (opt && typeof opt === 'object' && Array.isArray(opt.values)) return opt.values;
      const seen = [];
      (product.variants || []).forEach((v) => {
        const value = v[`option${idx + 1}`];
        if (value && seen.indexOf(value) === -1) seen.push(value);
      });
      return seen;
    };
    this.sizeValues = valuesAt(this.sizeIdx);
    this.depthValues = valuesAt(this.depthIdx);

    this.selectedSize = this.sizeValues.find((s) => this.sizeAvailable(s)) || this.sizeValues[0] || null;
    this.selectedDepth =
      this.depthValues.find((d) => this.depthAvailable(d)) || this.depthValues[0] || null;

    this.renderPills();
    this.updateVariant();
  }

  variantMatches(variant, size, depth) {
    if (this.sizeIdx >= 0 && size && variant[`option${this.sizeIdx + 1}`] !== size) return false;
    if (this.depthIdx >= 0 && depth && variant[`option${this.depthIdx + 1}`] !== depth) return false;
    return true;
  }

  sizeAvailable(size) {
    return (this.product.variants || []).some((v) => this.variantMatches(v, size, null) && v.available);
  }

  depthAvailable(depth) {
    return (this.product.variants || []).some(
      (v) => this.variantMatches(v, this.selectedSize, depth) && v.available
    );
  }

  renderPills() {
    const makePill = (value, label, selected, available, onSelect) => {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'gallery-3d__pill';
      pill.textContent = label;
      pill.dataset.value = value;
      pill.setAttribute('aria-pressed', String(selected));
      pill.classList.toggle('is-selected', selected);
      if (!available) {
        pill.classList.add('is-disabled');
        pill.setAttribute('aria-label', `${label} — ${this.strings.unavailable}`);
      }
      pill.addEventListener('click', onSelect);
      return pill;
    };

    this.refs.panelSizes.textContent = '';
    this.sizeValues.forEach((size) => {
      this.refs.panelSizes.appendChild(
        makePill(size, prettySize(size), size === this.selectedSize, this.sizeAvailable(size), () => {
          this.selectedSize = size;

          if (this.selectedDepth && !this.depthAvailable(this.selectedDepth)) {
            this.selectedDepth =
              this.depthValues.find((d) => this.depthAvailable(d)) || this.selectedDepth;
          }
          this.renderPills();

          const next = this.refs.panelSizes.querySelector(`[data-value="${CSS.escape(size)}"]`);
          if (next) next.focus({ preventScroll: true });
          this.updateVariant();
        })
      );
    });
    this.refs.panelSizesWrap.hidden = this.sizeValues.length === 0;

    this.refs.panelDepth.textContent = '';
    this.depthValues.forEach((depth) => {
      this.refs.panelDepth.appendChild(
        makePill(depth, depth, depth === this.selectedDepth, this.depthAvailable(depth), () => {
          this.selectedDepth = depth;
          this.renderPills();
          const next = this.refs.panelDepth.querySelector(`[data-value="${CSS.escape(depth)}"]`);
          if (next) next.focus({ preventScroll: true });
          this.updateVariant();
        })
      );
    });
    this.refs.panelDepthWrap.hidden = this.depthValues.length === 0;
  }

  updateVariant() {

    const variants = (this.product && this.product.variants) || [];
    this.variant =
      variants.find((v) => this.variantMatches(v, this.selectedSize, this.selectedDepth)) || null;

    const money = (cents) =>
      window.AirX && window.AirX.formatMoney ? window.AirX.formatMoney(cents) : `$${(cents / 100).toFixed(2)}`;

    if (this.variant) {
      this.refs.panelPrice.textContent = money(this.variant.price);
    } else if (this.product && typeof this.product.price === 'number') {
      this.refs.panelPrice.textContent = (this.strings.fromPrice || '[price]').replace(
        '[price]',
        money(this.product.price)
      );
    } else {
      this.refs.panelPrice.textContent = '';
    }

    const purchasable = !!(this.variant && this.variant.available);
    this.refs.panelAtc.disabled = !purchasable;
    this.refs.panelAtcLabel.textContent = purchasable ? this.strings.panelAdd : this.strings.soldOut;
  }

  async addToCart() {
    if (!this.variant || !this.variant.available || !window.AirX) return;
    const btn = this.refs.panelAtc;
    if (btn.classList.contains('btn--loading')) return;

    this.showPanelError('');
    btn.classList.add('btn--loading');
    try {
      const quantity = Math.max(1, parseInt(this.refs.panelQty.value, 10) || 1);
      await window.AirX.cart.add({ id: this.variant.id, quantity });

      if (!document.querySelector('cart-drawer')) {
        const added = (window.AirXStrings && window.AirXStrings.addedToCart) || 'Added';
        this.refs.panelAtcLabel.textContent = added;
        this.toast(added, 3000);
        this.setTimer(() => {
          if (this.panelOpen && this.product) this.updateVariant();
        }, 2000);
      }
    } catch (err) {
      this.showPanelError((err && err.message) || this.strings.errorCart);
    } finally {
      btn.classList.remove('btn--loading');
    }
  }

  toggleExcerpt() {
    this.excerptExpanded = !this.excerptExpanded;
    this.refs.panelExcerptText.textContent = this.excerptExpanded ? this.excerptFull : this.excerptShort;
    this.refs.panelMore.textContent = this.excerptExpanded ? this.strings.less : this.strings.more;
    this.refs.panelMore.setAttribute('aria-expanded', String(this.excerptExpanded));
  }

  toggleMap() {
    if (this.mapOpen) this.closeMap();
    else this.openMap();
  }

  openMap() {
    if (!this.entered || this.mapOpen || this.panelOpen || this.focusing || this.focusedMesh || this.helpOpen) return;
    this.mapOpen = true;
    this.buildMapSvg();
    this.refs.map.hidden = false;
    requestAnimationFrame(() => this.refs.map.classList.add('is-open'));
    this.relockOnResume = !this.isTouch && this.controls.isLocked();
    this.controls.disable();
    if (window.AirX) window.AirX.trapFocus(this.refs.map, this.refs.mapClose);
    this.updateCrosshair();
  }

  closeMap() {
    if (!this.mapOpen) return;
    this.mapOpen = false;
    this.refs.map.classList.remove('is-open');
    this.setTimer(() => {
      if (!this.mapOpen) this.refs.map.hidden = true;
    }, 260);
    if (window.AirX) window.AirX.releaseFocus(this.refs.map);
    if (this.entered && !this.focusedMesh && !this.openDrawers.size) {
      this.controls.enable();
      if (this.relockOnResume) {
        this.relockOnResume = false;
        this.controls.lock();
      }
    }
    this.updateCrosshair();
  }

  buildMapSvg() {
    const plan = this.world.floorPlan();
    this.mapPlan = plan;
    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.classList.add('gallery-3d__map-plan');

    if (plan.ring && plan.ring.length) {
      const ring = document.createElementNS(NS, 'path');
      let d = '';
      plan.ring.forEach((p, k) => {
        d += (k ? 'L' : 'M') + (p.x * 100).toFixed(2) + ' ' + (p.y * 100).toFixed(2);
      });
      ring.setAttribute('d', d + 'Z');
      ring.classList.add('gallery-3d__map-ring');
      svg.appendChild(ring);
    }
    (plan.spokes || []).forEach((s) => {
      const line = document.createElementNS(NS, 'line');
      line.setAttribute('x1', (s.x1 * 100).toFixed(2));
      line.setAttribute('y1', (s.y1 * 100).toFixed(2));
      line.setAttribute('x2', (s.x2 * 100).toFixed(2));
      line.setAttribute('y2', (s.y2 * 100).toFixed(2));
      line.classList.add('gallery-3d__map-spoke');
      svg.appendChild(line);
    });
    if (plan.hub) {
      const hub = document.createElementNS(NS, 'circle');
      hub.setAttribute('cx', (plan.hub.x * 100).toFixed(2));
      hub.setAttribute('cy', (plan.hub.y * 100).toFixed(2));
      hub.setAttribute('r', (plan.hub.r * 100).toFixed(2));
      hub.classList.add('gallery-3d__map-hub');
      svg.appendChild(hub);
    }

    plan.wings.forEach((wing) => {
      const node = document.createElementNS(NS, 'g');
      node.classList.add('gallery-3d__map-node');
      node.setAttribute('transform', `translate(${wing.x * 100} ${wing.y * 100})`);
      node.setAttribute('role', 'button');
      node.setAttribute('tabindex', '0');
      node.setAttribute(
        'aria-label',
        (this.strings.travelTo || '[wing]').replace('[wing]', wing.title)
      );

      const ring = document.createElementNS(NS, 'circle');
      ring.setAttribute('r', '5');
      node.appendChild(ring);

      const label = document.createElementNS(NS, 'text');
      label.setAttribute('y', '11');
      label.setAttribute('text-anchor', 'middle');
      label.textContent = wing.title;
      node.appendChild(label);

      const travel = () => this.travelTo(wing);
      node.addEventListener('click', travel);
      node.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          travel();
        }
      });
      svg.appendChild(node);
    });

    this.mapPlayerDot = document.createElementNS(NS, 'circle');
    this.mapPlayerDot.setAttribute('r', '2.4');
    this.mapPlayerDot.classList.add('gallery-3d__map-player');
    svg.appendChild(this.mapPlayerDot);

    this.refs.mapSvg.textContent = '';
    this.refs.mapSvg.appendChild(svg);
    this.updateMapPlayer();
  }

  updateMapPlayer() {
    if (!this.mapPlan || !this.mapPlayerDot) return;
    const pos = this.mapPlan.player(this.camera.position);
    this.mapPlayerDot.setAttribute('cx', String(pos.x * 100));
    this.mapPlayerDot.setAttribute('cy', String(pos.y * 100));
  }

  async travelTo(wing) {

    const index =
      wing && wing.index != null && this.wings[wing.index]
        ? wing.index
        : this.wings.findIndex((w) => w.key === (wing && wing.key));
    if (index < 0 || !this.wings[index]) return;
    this.travelSeq += 1;
    const key = this.wings[index].key;
    this.closeMap();

    const veil = this.refs.veil;
    veil.hidden = false;
    void veil.offsetWidth;
    veil.classList.add('is-active');
    await new Promise((resolve) => this.setTimer(resolve, 300));
    if (this.disposed) return;

    await this.buildWing(index);
    if (this.disposed) return;

    const entrance = (this.world.wingEntrances || [])[index];
    if (entrance && entrance.key === key) {
      this.controls.teleport(entrance.position, entrance.yaw);

      this.syncZones(true);
    }

    veil.classList.remove('is-active');
    this.setTimer(() => {
      if (!veil.classList.contains('is-active')) veil.hidden = true;
    }, 340);
  }

  handleKey(e) {
    if (!this.entered || this.disposed) return;
    if (this.gateOpen) return;
    if (e.key === 'Escape') {
      if (this.openDrawers.size) return;
      if (this.panelOpen || this.focusedMesh) {
        e.preventDefault();
        this.exitFocus();
      } else if (this.mapOpen) {
        e.preventDefault();
        this.closeMap();
      } else if (this.helpOpen) {
        e.preventDefault();
        this.enter();
      }
      return;
    }

    if (/^(KeyW|KeyA|KeyS|KeyD|ArrowUp|ArrowDown|ArrowLeft|ArrowRight)$/.test(e.code)) {
      this.hideHint();
    }
    if ((e.key === 'm' || e.key === 'M') && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const target = e.target;
      if (target && /^(input|textarea|select)$/i.test(target.tagName)) return;
      if (this.panelOpen || this.helpOpen || this.openDrawers.size) return;
      this.toggleMap();
    }
  }

  toast(message, ms) {
    const el = this.refs.toast;
    if (!el || !message) return;
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.timers.delete(this.toastTimer);
    }
    el.textContent = message;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    this.toastTimer = this.setTimer(() => {
      el.classList.remove('is-visible');
      this.setTimer(() => {
        if (!el.classList.contains('is-visible')) el.hidden = true;
      }, 320);
    }, ms);
  }

  teardown3D() {
    this.stopLoop();
    if (this.fetchAbort) this.fetchAbort.abort();
    if (this.wingsAbort) this.wingsAbort.abort();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.viewObserver) this.viewObserver.disconnect();
    if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
    if (this.refs.stage && this.onStageClick) {
      this.refs.stage.removeEventListener('click', this.onStageClick);
    }
    if (this.refs.stage && this.onStageRelock) {
      this.refs.stage.removeEventListener('click', this.onStageRelock);
    }

    if (this.refs.enterBtn && this.onEnterClick) {
      this.refs.enterBtn.removeEventListener('click', this.onEnterClick);
    }
    if (this.refs.hudMap && this.onHudMapClick) {
      this.refs.hudMap.removeEventListener('click', this.onHudMapClick);
    }
    if (this.refs.hudHelp && this.onHudHelpClick) {
      this.refs.hudHelp.removeEventListener('click', this.onHudHelpClick);
    }
    if (this.onStageFirstMove) {
      if (this.refs.stage) this.refs.stage.removeEventListener('pointerdown', this.onStageFirstMove);
      if (this.refs.joystick) this.refs.joystick.removeEventListener('pointerdown', this.onStageFirstMove);
    }
    if (this.moveCleanups) {
      this.moveCleanups.forEach((fn) => fn());
      this.moveCleanups = null;
    }
    if (this.refs.mapClose && this.onMapCloseClick) {
      this.refs.mapClose.removeEventListener('click', this.onMapCloseClick);
    }
    if (this.refs.panelClose && this.onPanelCloseClick) {
      this.refs.panelClose.removeEventListener('click', this.onPanelCloseClick);
    }
    if (this.refs.panelMore && this.onPanelMoreClick) {
      this.refs.panelMore.removeEventListener('click', this.onPanelMoreClick);
    }
    if (this.refs.panelAtc && this.onPanelAtcClick) {
      this.refs.panelAtc.removeEventListener('click', this.onPanelAtcClick);
    }
    if (this.controls) {
      try {
        this.controls.dispose();
      } catch (err) {

      }
      this.controls = null;
    }
    if (this.art) {
      try {
        this.art.dispose();
      } catch (err) {

      }
      this.art = null;
    }
    this.wingState.clear();
    if (this.wingBuilt) this.wingBuilt.clear();
    if (this.world) {
      try {
        this.world.dispose();
      } catch (err) {

      }
      this.world = null;
    }
    if (this.renderer) {
      this.renderer.domElement.removeEventListener('webglcontextlost', this.onContextLost);
      this.renderer.domElement.removeEventListener('webglcontextrestored', this.onContextRestored);
      this.renderer.dispose();
      if (this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
      this.renderer = null;
    }
  }

  teardown() {
    if (this.disposed) return;
    this.savePos();
    this.disposed = true;
    this.timers.forEach((id) => clearTimeout(id));
    this.timers.clear();
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:section:unload', this.onSectionUnload);
    document.removeEventListener('pointerlockchange', this.onLockChange);
    if (this.onPageHide) window.removeEventListener('pagehide', this.onPageHide);
    if (this.onContextMenu) this.removeEventListener('contextmenu', this.onContextMenu);
    if (window.AirX) {
      if (this.onDrawerOpen) window.AirX.off('drawer:open', this.onDrawerOpen);
      if (this.onDrawerClose) window.AirX.off('drawer:close', this.onDrawerClose);
      if (this.panelOpen) window.AirX.releaseFocus(this.refs.panel);
      if (this.mapOpen) window.AirX.releaseFocus(this.refs.map);
    }
    this.teardown3D();
  }
}

if (!customElements.get('gallery-3d')) customElements.define('gallery-3d', Gallery3D);
